import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { RpcInputOptions } from '@core-controls/components/rpc-input/models/rpc-input-options';
import { ValidationService } from '@core-validation/validation.service';
import { UpdatePasswordModel } from '../../interfaces/customer-settings/update-password-model';
import { UpdatePasswordRequest } from '../../interfaces/customer-settings/update-password-request';
import { Subject, takeUntil } from 'rxjs';
import { PasswordComponent } from '../../../../../core-modules/controls/components/password/password.component';
import { RpcButtonComponent } from '../../../../../core-modules/controls/components/rpc-button/rpc-button.component';

@Component({
    selector: 'change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ReactiveFormsModule, PasswordComponent, RpcButtonComponent]
})
export class ChangePasswordComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() isAdditionalProfile: boolean;
    @Output() passwordChanged: EventEmitter<UpdatePasswordModel> = new EventEmitter<UpdatePasswordModel>();

    public changePasswordForm: UntypedFormGroup;
    public oldPasswordControlOptions: RpcInputOptions;
    public newPasswordControlOptions: RpcInputOptions;
    public confirmPasswordControlOptions: RpcInputOptions;

    private readonly unsubscribe$ = new Subject<void>();

    constructor(
        private readonly validationService: ValidationService) {
    }

    public get isUpdateButtonDisabled(): boolean {
        return !this.changePasswordForm.dirty
            || this.changePasswordForm.invalid
            || (!this.isAdditionalProfile && this.changePasswordForm.controls.oldPassword.value == null)
            || this.changePasswordForm.controls.newPassword.value == null
            || this.changePasswordForm.controls.confirmPassword.value == null;
    }

    public ngOnInit(): void {
        this.buildForm();
    }

    public ngAfterViewInit(): void {
        this.changePasswordForm.get('newPassword')?.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.changePasswordForm.get('confirmPassword').updateValueAndValidity();
                this.changePasswordForm.get('newPassword').markAsTouched();
            });
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public cancel(): void {
        this.resetForm();
    }

    public save = () => {
        if (this.changePasswordForm.invalid) {
            return;
        }

        this.passwordChanged.emit({ data: this.changePasswordForm.getRawValue() as UpdatePasswordRequest });

        this.resetForm();
    };

    private buildForm(): void {
        const requiredValidator = {
            message: 'CHANGE_PASSWORD.VALIDATION_ERRORS.PASSWORD_REQUIRED',
            showError: (control: UntypedFormControl) => control.hasError('required'),
            validator: Validators.required
        };

        this.changePasswordForm = new UntypedFormGroup({});
        this.oldPasswordControlOptions = {
            formGroup: this.changePasswordForm,
            controlName: 'oldPassword',
            validatorsMap: [requiredValidator]
        };

        this.newPasswordControlOptions = {
            formGroup: this.changePasswordForm,
            controlName: 'newPassword',
            validatorsMap: [
                requiredValidator,
                {
                    message: 'CHANGE_PASSWORD.VALIDATION_ERRORS.NUMBER_REQIRED',
                    showError: (control: UntypedFormControl) => control.hasError('noNumber'),
                    validator: this.validationService.getPasswordValidator()
                },
                {
                    message: 'CHANGE_PASSWORD.VALIDATION_ERRORS.CAPITAL_REQUIRED',
                    showError: (control: UntypedFormControl) => control.hasError('noCapitalCase'),
                    validator: this.validationService.getPasswordValidator()
                },
                {
                    message: 'CHANGE_PASSWORD.VALIDATION_ERRORS.SMALL_REQUIRED',
                    showError: (control: UntypedFormControl) => control.hasError('noSmallCase'),
                    validator: this.validationService.getPasswordValidator()
                },
                {
                    message: 'CHANGE_PASSWORD.VALIDATION_ERRORS.SPECIAL_REQIRED',
                    showError: (control: UntypedFormControl) => control.hasError('noSpecialCharacter'),
                    validator: this.validationService.getPasswordValidator()
                },
                {
                    message: 'CHANGE_PASSWORD.VALIDATION_ERRORS.MINIMUM_LENGTH',
                    showError: (control: UntypedFormControl) => control.hasError('minlength'),
                    validator: this.validationService.getPasswordValidator()
                },
                {
                    message: 'CHANGE_PASSWORD.VALIDATION_ERRORS.MAXIMUM_LENGTH',
                    showError: (control: UntypedFormControl) => control.hasError('maxlength'),
                    validator: this.validationService.getPasswordValidator()
                },
                {
                    message: 'CHANGE_PASSWORD.VALIDATION_ERRORS.PASSWORDS_MATCH',
                    showError: (control: UntypedFormControl) => control.hasError('matching'),
                    validator: this.validationService.getNotMatchValidator('oldPassword')
                }
            ]
        };

        this.confirmPasswordControlOptions = {
            formGroup: this.changePasswordForm,
            controlName: 'confirmPassword',
            validatorsMap: [
                requiredValidator,
                {
                    message: 'CHANGE_PASSWORD.VALIDATION_ERRORS.PASSWORDS_NOT_MATCH',
                    showError: (control: UntypedFormControl) => control.hasError('notMatching'),
                    validator: this.validationService.getMatchValidator('newPassword')
                }
            ]
        };
    }

    private resetForm(): void {
        this.changePasswordForm.reset();
    }
}
